import React, {useMemo} from 'react';
import {Grid} from '@mui/material';
import {
  BuilderColumnWrapper,
  BuilderSourceColumnHeader,
  BuilderDestinationColumnHeader,
} from '../CreateBuilder/BuilderSourceDestination/BuilderSourceDestination';
import {BuilderProps} from '../CreateBuilder/BuilderSourceDestination/builderValidators';
import {BuilderPlanFormsStyled} from '../CreateBuilder/BuilderSourceDestination/BuilderPlanForm';
import {ContributeSourceParent} from './ContributeSourceParent';
import {ContributeDestinationParent} from './ContributeDestinationParent';
import {POTS_CASH} from '../../../utils/potsFilter';
import {ContributeCashSourceRow} from './ContributeCashSourceRow';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {contributeFieldType} from './builderContributeFields';
import {BuilderPot} from '../../../schema-types/BuilderPot';

import {BuilderParserStep} from '../BuilderParserStep/BuilderParserStep';
import {contributionSources, contributionDestinations} from './BuilderContributeHelpers';
import {
  changeContributeFromFactory,
  getContributeDestinationFromSelection,
} from '../hooks/builderContributeHooks';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {useBuilderPlanTypes} from '../CreateBuilder/CreateBuilderHelpers';
import {UPDATES} from '../../../shared/UPDATES';
import {useAutoForm, useAutoField} from '../../../components/AutoForm/useAutoForm';
import {useContributionPotsQuery} from './useContributionPotsQuery';

export const Contribute: React.FC<BuilderProps> = ({sourceDestinationLabels}) => {
  const {builderDetails, isSPPDA, builderReviewId} = useBuilderContext();
  const {values} = useAutoForm<builderFieldsType>();

  const {sourcePots, destinationPots} = useContributionPotsQuery();

  const [contributeFieldObj] = useAutoField<builderFieldsType['contribute'] | undefined>(
    'contribute'
  );

  const [contributeFromObj, contributeFromMeta, contributeFromHelpers] = useAutoField<
    contributeFieldType['contributeFrom']
  >('contribute.contributeFrom');
  const {mainPlanTypes} = useBuilderPlanTypes(values);
  const [contributeToFieldObj, contributeToMeta, contributeToHelpers] = useAutoField<
    contributeFieldType['contributeTo'] | undefined
  >('contribute.contributeTo');
  const [, , destinationValuesHelpers] = useAutoField<
    contributeFieldType['destinationValues']
  >('contribute.destinationValues');

  // current accounts (bank accounts) cannot be contribution sources
  const contributeFromPots: BuilderPot[] = sourcePots.filter((x) => !x.productIsBank);
  const sourcePotsList = useMemo(() => {
    const pots = contributionSources({
      contributeFromPots,
      contributeTo: contributeToFieldObj.value,
      isSppDa: isSPPDA,
    });
    return pots;
  }, [contributeFromPots, contributeToFieldObj.value, isSPPDA]);
  const changeContributeFrom = changeContributeFromFactory({
    contributeFromPots,
    contributeFromHelpers,
  });
  const destPots = useMemo(() => {
    const pots = contributionDestinations({
      destinationPots,
      contributeFieldObj,
      builderDetails,
      isSPPDA,
    });
    return pots;
  }, [
    destinationPots,
    contributeFieldObj.value?.contributeFrom?.id,
    builderDetails,
    isSPPDA,
  ]);

  if (!values.mainPlanType) {
    return null;
  }
  if (!mainPlanTypes.length) {
    return <BuilderParserStep errorText="There are no plans to contribute from." />;
  }

  return (
    <BuilderColumnWrapper>
      <Grid item xs={6} xl={5.8} data-test="Source Choices">
        <BuilderSourceColumnHeader
          fromError={contributeFromMeta.error}
          sourceLabel={sourceDestinationLabels?.first}
        />
        <BuilderPlanFormsStyled>
          <ContributeCashSourceRow
            contributeFromId={contributeFromObj.value?.id}
            handleChange={() =>
              contributeFromHelpers.setValue(UPDATES._292, {
                id: POTS_CASH,
                parentPotId: undefined,
                singleClientAmount: undefined,
                recurringClientAmount: undefined,
                maxAmount: 0,
                initialFee: null,
                legacyInitialFee: null,
                legacyInitialEmployerFee: null,
              })
            }
          />
          {sourcePotsList.map((pot) => (
            <ContributeSourceParent
              key={pot.id}
              pot={pot}
              contributeFromId={contributeFromObj.value?.id}
              greyed={pot.id === contributeToFieldObj.value}
              sameReview={pot.review?.id === builderReviewId}
              handleChange={(childId) => changeContributeFrom(childId)}
            />
          ))}
        </BuilderPlanFormsStyled>
      </Grid>
      <Grid item xs={6} xl={5.8} data-test="Destination Choices">
        <BuilderDestinationColumnHeader
          toError={contributeToMeta.error}
          destinationLabel={sourceDestinationLabels?.second}
        />
        <BuilderPlanFormsStyled>
          {destPots.map((pot) => (
            <ContributeDestinationParent
              key={pot.id}
              pot={pot}
              contributeToId={contributeToFieldObj.value}
              sameReview={pot.review?.id === builderReviewId}
              handleChange={(selectedPot) => {
                contributeToHelpers.setValue(UPDATES._293, pot.id);
                destinationValuesHelpers.setValue(
                  UPDATES._293,
                  getContributeDestinationFromSelection({pot: selectedPot})
                );
              }}
            />
          ))}
        </BuilderPlanFormsStyled>
      </Grid>
    </BuilderColumnWrapper>
  );
};
